import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import { fmtNumber, fmtPeriode, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { GenererBulletinUseCase } from '@application/usecases/GenererBulletinUseCase';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import {
  CHAMPS_NUMERIQUES,
  exigeValidationRh,
  exporterCsv,
  fusionnerLigne,
  importerCsv,
  ligneVide,
  saisieCsvTemplate,
  totalRetenues,
  validerLigne,
  versSaisieBulletin,
  type ChampNumeriqueSaisie,
  type LigneSaisieMensuelle,
} from '@domain/paie/ElementsVariables';
import {
  avantageNatureParSalarie,
  avantagesFromImmobilisations,
  avantagesFromVehicules,
} from '@domain/paie/AvantageNature';
import {
  bloquerGenerationLot,
  calculerStatutValidationRh,
  libelleStatutValidationRh,
  type LigneSaisieAvecValidation,
} from '@domain/paie/ValidationRhSaisie';
import { downloadCsv } from '@presentation/shell/download';
import { onExclusive } from '@presentation/shell/viewDom';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { canMutateComptaRecords, canMutateRecords } from '@application/access/ViewAccessPolicy';
import { loadRegister } from '@presentation/shell/localCrud';
import { loadComptesPaie } from '@application/comptabilite/ComptesPaieStore';
import { COMPTES_PAIE_PAR_DEFAUT } from '@domain/comptabilite/ComptesPaie';
import { isModuleExcluded, PAIE_COMPTA_MODULE } from '@application/licence/ModuleGate';
import type { Ecriture } from '@shared/types/Ecriture';
import { loadPeriodesCloturees } from '@application/comptabilite/PeriodesCloturees';
import {
  assertEcrituresModifiables,
  corrigerEcrituresPaie,
  premierePeriodeOuverte,
} from '@domain/comptabilite/ExtournePaie';
import { runAtomicWrites } from '@application/persistence/AtomicBatch';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { bulletinsParSalarie, decisionRecalculBulletins } from '@domain/paie/BulletinsUniques';
import { estPayableSurPeriode, joursPayesDansMois } from '@domain/paie/ProrataPaie';
import { JOURS_MOIS_PAIE } from '@shared/constants/Taux';
import { periodeCouranteFromTenant } from '@presentation/shell/periodeCourante';

interface SaisiePeriodeStockee {
  inclure13e: boolean;
  lignes: Record<string, Partial<LigneSaisieMensuelle>>;
}

function namespace(periode: string): string {
  return `paie.saisie.${periode}`;
}

function cellNum(
  salarieId: string,
  field: ChampNumeriqueSaisie,
  value: number,
  width = 72,
  disabled = false,
): string {
  return `<td class="num"><input type="number" min="0" step="1" data-salarie="${esc(salarieId)}" data-field="${field}" value="${value}" ${disabled ? 'disabled' : ''} style="width:${width}px;padding:4px 6px;border:1px solid #DAE0E4;border-radius:4px;font-family:inherit;text-align:right;"></td>`;
}

export const saisieController: ViewController = async (section, ctx, shell) => {
  const canEdit = canMutateComptaRecords(shell.permission);
  const canCalculer = canMutateRecords(shell.permission);
  /** Approuver / refuser une saisie relève du RH, pas de la comptabilité. */
  const canValiderRh = canMutateRecords(shell.permission);
  const tousSalaries = await ctx.salaries.list();
  const parId = new Map(tousSalaries.map((s) => [s.id, s]));
  const tenant = ctx.tenantContext.tryGetTenant();
  let current = periodeCouranteFromTenant(tenant);
  /**
   * Effectif à payer sur le mois affiché : les salariés en poste, plus ceux
   * partis en cours de mois (dernier bulletin), moins ceux dont le contrat
   * commence après la fin du mois.
   */
  const salariesDeLaPeriode = (): Salarie[] =>
    tousSalaries.filter((s) => {
      const enPoste = s.statut === 'actif' || s.statut === 'suspendu' || s.statut === 'conge';
      const sortiCeMois = s.statut === 'sortie' && Boolean(s.date_sortie);
      if (!enPoste && !sortiCeMois) return false;
      return estPayableSurPeriode(s, current);
    });
  let salaries = salariesDeLaPeriode();
  let searchQuery = '';
  const draft = new Map<string, LigneSaisieMensuelle>();

  const tbody = section.querySelector<HTMLElement>('#saisie-tbody');
  const empty = section.querySelector<HTMLElement>('#saisie-empty');
  const display = section.querySelector('#periode-display');
  const check13e = section.querySelector<HTMLInputElement>('#inclure-13e');
  const searchInput = section.querySelector<HTMLInputElement>('#saisie-search');

  const charger = async (): Promise<void> => {
    draft.clear();
    salaries = salariesDeLaPeriode();
    const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
    const stock = await SecureTenantStore.getJson<SaisiePeriodeStockee>(tid, namespace(current), {
      inclure13e: false,
      lignes: {},
    });
    if (check13e) check13e.checked = stock.inclure13e;
    for (const s of salaries) {
      draft.set(s.id, fusionnerLigne(s, stock.lignes[s.id]));
    }
  };

  const persister = async (): Promise<void> => {
    if (!canEdit) return;
    const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
    const lignes: Record<string, Partial<LigneSaisieMensuelle>> = {};
    for (const [id, ligne] of draft) {
      const vierge = ligneVide(parId.get(id)!);
      const diff: Partial<LigneSaisieMensuelle> = {};
      let utile = false;
      for (const champ of CHAMPS_NUMERIQUES) {
        if (ligne[champ] !== vierge[champ]) {
          diff[champ] = ligne[champ];
          utile = true;
        }
      }
      if (ligne.motif_prime_extra) {
        diff.motif_prime_extra = ligne.motif_prime_extra;
        utile = true;
      }
      if (ligne.validation_rh && ligne.validation_rh !== 'ok') {
        diff.validation_rh = ligne.validation_rh;
        utile = true;
      }
      if (utile) lignes[id] = diff;
    }
    await SecureTenantStore.setJson(tid, namespace(current), {
      inclure13e: Boolean(check13e?.checked),
      lignes,
    });
  };

  const lireLigne = (salarie: Salarie): LigneSaisieMensuelle => {
    const actuelle = draft.get(salarie.id) ?? ligneVide(salarie);
    if (!tbody) return actuelle;
    const nombre = (field: ChampNumeriqueSaisie): number => {
      const input = tbody.querySelector<HTMLInputElement>(
        `input[data-salarie="${CSS.escape(salarie.id)}"][data-field="${field}"]`,
      );
      const n = Number(input?.value ?? actuelle[field]);
      return Number.isFinite(n) ? n : 0;
    };
    const motif = tbody.querySelector<HTMLInputElement>(
      `input[data-salarie="${CSS.escape(salarie.id)}"][data-field="motif_prime_extra"]`,
    );
    const ligne: LigneSaisieMensuelle = {
      ...actuelle,
      salaire_base: nombre('salaire_base'),
      hs_110: nombre('hs_110'),
      hs_125: nombre('hs_125'),
      hs_150: nombre('hs_150'),
      hs_200: nombre('hs_200'),
      prime: nombre('prime'),
      prime_extra: nombre('prime_extra'),
      motif_prime_extra: motif?.value ?? actuelle.motif_prime_extra,
      absences_jours: nombre('absences_jours'),
      avance: nombre('avance'),
      acompte: nombre('acompte'),
      quinzaine: nombre('quinzaine'),
      pret: nombre('pret'),
    };
    draft.set(salarie.id, ligne);
    return ligne;
  };

  const rafraichirTotaux = (salarieId: string): void => {
    const salarie = parId.get(salarieId);
    if (!salarie || !tbody) return;
    const ligne = lireLigne(salarie);
    const retenues = tbody.querySelector(`[data-retenues="${CSS.escape(salarieId)}"]`);
    if (retenues) retenues.textContent = fmtNumber(totalRetenues(ligne));
    const statut = tbody.querySelector(`[data-validation="${CSS.escape(salarieId)}"]`);
    if (statut) {
      const st = calculerStatutValidationRh(ligne, salarie.salaire_base);
      statut.textContent = libelleStatutValidationRh(st);
      (statut as HTMLElement).style.color =
        st === 'ok' || st === 'approuve' ? 'var(--gris)' : 'var(--orange, #D68910)';
    }
  };

  const render = (): void => {
    if (display) display.textContent = fmtPeriode(current);
    const q = searchQuery.toLowerCase().trim();
    const visible = q
      ? salaries.filter((s) => `${s.prenom} ${s.nom} ${s.matricule}`.toLowerCase().includes(q))
      : salaries;
    if (empty) empty.style.display = salaries.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML = visible
      .map((s) => {
        const l = (draft.get(s.id) ?? ligneVide(s)) as LigneSaisieAvecValidation;
        const st = calculerStatutValidationRh(l, s.salaire_base);
        const alerte = st === 'a_controler' || st === 'refuse';
        const actionsRh =
          canValiderRh && st === 'a_controler'
            ? `<button type="button" class="btn btn-ghost btn-sm" data-rh-approuver="${esc(s.id)}" title="Approuver">✓</button>
               <button type="button" class="btn btn-ghost btn-sm" data-rh-refuser="${esc(s.id)}" title="Refuser">✗</button>`
            : '';
        const joursPayes = joursPayesDansMois(current, s.date_embauche, s.date_sortie);
        const moisPartiel = joursPayes < JOURS_MOIS_PAIE;
        return `<tr data-row="${esc(s.id)}">
        <td><strong>${esc(s.prenom)} ${esc(s.nom)}</strong><br><span class="mono" style="font-size:11px;color:var(--gris);">${esc(s.matricule)}</span></td>
        <td class="num"${moisPartiel ? ' title="Mois incomplet : salaire proratisé en trentièmes"' : ''}><span style="color:${moisPartiel ? 'var(--orange, #D68910)' : 'var(--gris)'};">${joursPayes}</span></td>
        ${cellNum(s.id, 'salaire_base', l.salaire_base, 96, !canEdit)}
        ${cellNum(s.id, 'hs_110', l.hs_110, 72, !canEdit)}
        ${cellNum(s.id, 'hs_125', l.hs_125, 72, !canEdit)}
        ${cellNum(s.id, 'hs_150', l.hs_150, 72, !canEdit)}
        ${cellNum(s.id, 'hs_200', l.hs_200, 72, !canEdit)}
        ${cellNum(s.id, 'prime', l.prime, 72, !canEdit)}
        ${cellNum(s.id, 'prime_extra', l.prime_extra, 72, !canEdit)}
        <td><input type="text" data-salarie="${esc(s.id)}" data-field="motif_prime_extra" value="${esc(l.motif_prime_extra)}" ${canEdit ? '' : 'disabled'} style="width:120px;padding:4px 6px;border:1px solid #DAE0E4;border-radius:4px;font-family:inherit;"></td>
        ${cellNum(s.id, 'absences_jours', l.absences_jours, 72, !canEdit)}
        ${cellNum(s.id, 'avance', l.avance, 72, !canEdit)}
        ${cellNum(s.id, 'acompte', l.acompte, 72, !canEdit)}
        ${cellNum(s.id, 'quinzaine', l.quinzaine, 72, !canEdit)}
        ${cellNum(s.id, 'pret', l.pret, 72, !canEdit)}
        <td class="num" data-retenues="${esc(s.id)}">${esc(fmtNumber(totalRetenues(l)))}</td>
        <td><span data-validation="${esc(s.id)}" style="font-size:11px;color:${alerte ? 'var(--orange, #D68910)' : 'var(--gris)'};">${esc(libelleStatutValidationRh(st))}</span> ${actionsRh}</td>
      </tr>`;
      })
      .join('');
  };

  onExclusive(searchInput, 'input', (e) => {
    searchQuery = (e.target as HTMLInputElement).value;
    render();
  });

  onExclusive(tbody, 'input', (e) => {
    const input = (e.target as HTMLElement).closest<HTMLInputElement>('input[data-salarie]');
    if (input?.dataset.salarie) {
      const sal = parId.get(input.dataset.salarie);
      if (sal) {
        const ligne = lireLigne(sal) as LigneSaisieAvecValidation;
        if (exigeValidationRh(ligne, sal.salaire_base) && ligne.validation_rh === 'approuve') {
          ligne.validation_rh = 'a_controler';
          draft.set(sal.id, ligne);
        }
      }
      rafraichirTotaux(input.dataset.salarie);
    }
  });
  onExclusive(tbody, 'change', () => void persister());
  onExclusive(tbody, 'click', (e) => {
    const approuver = (e.target as HTMLElement).closest<HTMLElement>('[data-rh-approuver]');
    const refuser = (e.target as HTMLElement).closest<HTMLElement>('[data-rh-refuser]');
    const id = approuver?.dataset.rhApprouver ?? refuser?.dataset.rhRefuser;
    if (!id) return;
    if (!canValiderRh) {
      ToastService.warning('Validation réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    const sal = parId.get(id);
    if (!sal) return;
    const ligne = {
      ...lireLigne(sal),
      validation_rh: approuver ? 'approuve' : 'refuse',
    } as LigneSaisieAvecValidation;
    draft.set(id, ligne);
    void persister().then(() => {
      render();
      ToastService.success(
        approuver ? `${sal.matricule} : saisie approuvée` : `${sal.matricule} : saisie refusée`,
      );
    });
  });

  await charger();
  render();

  const w = window as unknown as Record<string, unknown>;
  w['changePeriode'] = (delta: number) => {
    void (async () => {
      await persister();
      const [y, m] = current.split('-').map(Number);
      const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
      current = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      await charger();
      render();
    })();
  };
  w['toggle13e'] = () => {
    void persister();
  };
  w['exporterSaisie'] = () => {
    const lignes = salaries.map((s) => lireLigne(s));
    downloadCsv(exporterCsv(lignes), `saisie_paie_${current}.csv`);
    ToastService.success(`Saisie de ${fmtPeriode(current)} exportée`);
  };
  w['downloadSaisieCsvTemplate'] = () => {
    downloadCsv(saisieCsvTemplate(), 'modele_saisie_paie.csv');
    ToastService.success('Modèle CSV téléchargé — matricules existants requis à l’import');
  };
  w['importerSaisie'] = (event: Event) => {
    if (!canEdit) {
      ToastService.warning('Saisie réservée aux profils autorisés');
      return;
    }
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;
    void file.text().then(async (texte) => {
      const { lignes, ignorees } = importerCsv(texte, salaries);
      for (const ligne of lignes) draft.set(ligne.salarie_id, ligne);
      render();
      await persister();
      if (input) input.value = '';
      ToastService.success(
        ignorees > 0
          ? `${lignes.length} ligne(s) importée(s), ${ignorees} matricule(s) inconnu(s) ignoré(s)`
          : `${lignes.length} ligne(s) importée(s)`,
      );
    });
  };
  /**
   * Un seul calcul à la fois : un double clic (ou un clic pendant la confirmation) lançait deux calculs
   * concurrents qui lisaient tous deux « aucun bulletin » et créaient chacun un bulletin par salarié.
   */
  let calculEnCours = false;
  w['calculerPaie'] = () => {
    if (calculEnCours) {
      ToastService.info('Un calcul de paie est déjà en cours, patientez.');
      return;
    }
    calculEnCours = true;
    calculerPaie()
      .catch((err: unknown) => {
        ToastService.error(
          `Calcul de la paie impossible : ${err instanceof Error ? err.message : String(err)}`,
        );
      })
      .finally(() => {
        calculEnCours = false;
      });
  };

  async function calculerPaie(): Promise<void> {
    if (!canCalculer) {
      ToastService.warning(
        'Le calcul de paie est réservé au gestionnaire RH et à l’administrateur',
      );
      return;
    }
    if (salaries.length === 0) {
      ToastService.warning('Aucun salarié actif à payer');
      return;
    }
    const tousBulletins = await ctx.bulletins.list();
    const existingBySalarie = bulletinsParSalarie(tousBulletins, current);
    const empreinte = (liste: readonly Bulletin[]): string =>
      liste
        .filter((b) => b.periode === current)
        .map((b) => `${b.id}:${b.statut ?? 'genere'}`)
        .sort()
        .join('|');
    const empreinteAvant = empreinte(tousBulletins);
    const options = { periode: current, inclure13e: Boolean(check13e?.checked) };
    const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
    /**
     * Offre Paie seule : sans la passerelle paie-comptabilité, la paie ne touche pas à la comptabilité
     * (ni comptes de paie, ni périodes clôturées, ni écritures) : ces appels seraient refusés par le serveur.
     */
    const avecCompta = !isModuleExcluded(ctx, PAIE_COMPTA_MODULE);
    const avecImmos = !isModuleExcluded(ctx, 'immobilisations');
    const [comptesPaie, cloturees] = avecCompta
      ? await Promise.all([loadComptesPaie(tid), loadPeriodesCloturees(tid)])
      : [COMPTES_PAIE_PAR_DEFAUT, [] as string[]];
    const [immos, vehicules] = await Promise.all([
      avecImmos
        ? loadRegister<{
            salarie_id: string;
            an_mensuel: number;
            statut: string;
          }>(tid, 'immobilisations')
        : Promise.resolve([]),
      loadRegister<{
        salarie_id?: string;
        avantage_nature_mensuel?: number;
        statut?: string;
      }>(tid, 'rh.vehicules'),
    ]);
    const avantageParSalarie = avantageNatureParSalarie([
      ...avantagesFromImmobilisations(immos),
      ...avantagesFromVehicules(vehicules),
    ]);

    type Cible = {
      s: (typeof salaries)[number];
      ligne: LigneSaisieMensuelle;
      mode: 'creer' | 'recalculer';
      /** Bulletins « générés » à remplacer (plusieurs si des doublons existent déjà). */
      anciens: Bulletin[];
    };
    const aGenerer: Cible[] = [];
    const ignores: string[] = [];
    for (const s of salaries) {
      const ligne = lireLigne(s);
      const erreur = validerLigne(ligne);
      if (erreur) {
        ToastService.error(`${s.matricule} — ${erreur}`);
        return;
      }
      const decision = decisionRecalculBulletins(existingBySalarie.get(s.id) ?? []);
      if (decision.action === 'ignorer') {
        ignores.push(`${s.matricule} (${decision.motif})`);
        continue;
      }
      aGenerer.push({
        s,
        ligne,
        mode: decision.action,
        anciens: decision.action === 'recalculer' ? decision.existants : [],
      });
    }
    const blocage = bloquerGenerationLot(
      aGenerer.map(({ s, ligne }) => ({ ligne, salaireFiche: s.salaire_base })),
    );
    if (blocage) {
      ToastService.error(blocage);
      return;
    }

    if (aGenerer.length === 0) {
      ToastService.info(
        ignores.length > 0
          ? `Aucun bulletin recalculable pour ${fmtPeriode(current)}. ${ignores.length} déjà validé(s)/payé(s).`
          : `Paie déjà calculée pour ${fmtPeriode(current)}`,
      );
      return;
    }

    const nbRecalc = aGenerer.filter((c) => c.mode === 'recalculer').length;
    if (nbRecalc > 0) {
      const ok = await confirmAction({
        host: section,
        title: 'Recalculer la paie',
        message: `${nbRecalc} bulletin(s) déjà généré(s) seront remplacés avec la saisie actuelle (avances, HS, primes…). Les bulletins validés ou payés ne sont pas modifiés.`,
        confirmLabel: 'Recalculer',
      });
      if (!ok) return;
      // Les bulletins ont pu changer pendant la confirmation (autre onglet, autre utilisateur) : on ne devine pas.
      if (empreinte(await ctx.bulletins.list()) !== empreinteAvant) {
        ToastService.warning(
          'Les bulletins de la période ont changé pendant la confirmation. Relancez le calcul.',
        );
        return;
      }
    }

    let created = 0;
    let recalcules = 0;
    const allEcritures: Ecriture[] = avecCompta ? await ctx.ecritures.list() : [];
    for (const { s, ligne, mode, anciens } of aGenerer) {
      const saisie = versSaisieBulletin(s, ligne, {
        ...options,
        avantage_nature: avantageParSalarie.get(s.id) ?? 0,
      });
      let resultat: ReturnType<GenererBulletinUseCase['execute']>;
      try {
        resultat = new GenererBulletinUseCase().execute({
          salarie: s,
          saisie,
          periode: current,
          comptesPaie,
          periodesCloturees: cloturees,
          bulletinsExistants: tousBulletins,
          tenantSettings: tenant?.parametres ?? null,
          formeJuridique: tenant?.forme_juridique ?? null,
        });
      } catch (err) {
        ToastService.error(
          `Bulletin ${s.matricule} refusé : ${err instanceof Error ? err.message : String(err)}`,
        );
        return;
      }
      const { bulletin } = resultat;
      const ecrituresBulletin = avecCompta ? resultat.ecritures : [];
      /**
       * FR-ECR-03 : période comptable clôturée = jamais de modification. Le recalcul d'un bulletin dont
       * les écritures sont dans une période clôturée extourne l'existant et comptabilise la correction
       * dans la première période ouverte ; sinon (période ouverte) l'ancien comportement est conservé.
       */
      const correctionCloturee =
        mode === 'recalculer' && anciens.length > 0 && cloturees.includes(current);
      let ecritures = ecrituresBulletin;
      let aSupprimer: typeof allEcritures = [];
      if (correctionCloturee) {
        try {
          const correction = corrigerEcrituresPaie({
            ecritures: allEcritures,
            bulletinCorrige: { ...bulletin, tenant_id: s.tenant_id },
            salarie: s,
            periodeImputation: premierePeriodeOuverte(current, cloturees),
            comptes: comptesPaie,
          });
          ecritures = [...correction.extournes, ...correction.corrections];
          allEcritures.push(...ecritures);
        } catch (err) {
          ToastService.error(`Correction de paie refusée ${s.matricule} : ${String(err)}`);
          return;
        }
      } else if (mode === 'recalculer' && anciens.length > 0) {
        aSupprimer = allEcritures.filter(
          (e) =>
            e.periode === current &&
            e.source === 'paie' &&
            (e.salarie_id === s.id || e.matricule === s.matricule),
        );
        try {
          assertEcrituresModifiables(aSupprimer, cloturees);
        } catch (err) {
          ToastService.error(String(err instanceof Error ? err.message : err));
          return;
        }
      }
      try {
        await runAtomicWrites([
          ...anciens.map((ancien) => ({
            table: 'bulletins',
            id: ancien.id,
            run: async () => {
              await ctx.bulletins.delete(ancien.id);
            },
            rollback: async () => {
              await ctx.bulletins.add(ancien);
            },
          })),
          ...aSupprimer.map((e) => ({
            table: 'ecritures',
            id: e.id,
            run: async () => {
              await ctx.ecritures.delete(e.id);
            },
            rollback: async () => {
              await ctx.ecritures.add(e);
            },
          })),
          {
            table: 'bulletins',
            id: bulletin.id,
            run: async () => {
              await ctx.bulletins.add({ ...bulletin, tenant_id: s.tenant_id, statut: 'genere' });
            },
            rollback: async () => {
              await ctx.bulletins.delete(bulletin.id);
            },
          },
          ...ecritures.map((e) => ({
            table: 'ecritures',
            id: e.id,
            run: async () => {
              await ctx.ecritures.add({ ...e, tenant_id: s.tenant_id });
            },
            rollback: async () => {
              await ctx.ecritures.delete(e.id);
            },
          })),
        ]);
      } catch (err) {
        ToastService.error(`Échec atomique bulletin ${s.matricule} : ${String(err)}`);
        return;
      }
      await recordAuditEvent(ctx, {
        action: mode === 'recalculer' ? 'update' : 'create',
        entity: 'bulletin',
        entity_id: bulletin.id,
        description:
          mode === 'recalculer'
            ? `Recalcul bulletin ${current} — ${s.matricule} (saisie à jour)`
            : `Bulletin ${current} — ${s.matricule}`,
      });
      if (mode === 'recalculer') recalcules += 1;
      else created += 1;
    }
    await persister();
    const parts = [
      created > 0 ? `${created} créé(s)` : null,
      recalcules > 0 ? `${recalcules} recalculé(s)` : null,
      ignores.length > 0 ? `${ignores.length} figé(s)` : null,
    ].filter(Boolean);
    ToastService.success(
      `Paie ${fmtPeriode(current)} : ${parts.join(', ')} — avances / HS / retenues de la saisie appliquées`,
    );
  }
};
